"use client"

import {
  FloatingPortal,
  useDismiss,
  useInteractions,
  useTransitionStyles,
} from "@floating-ui/react"
import { useReducedMotion } from "motion/react"
import {
  cloneElement,
  isValidElement,
  type MouseEventHandler,
  type ReactElement,
  type ReactNode,
} from "react"
import { DURATION_BASE, DURATION_QUICK, EASE_OUT_CSS } from "../../lib/ease"
import { cn } from "../../lib/utils"
import { V2_MENU_SURFACE_CLASS } from "./menu"
import { matchReferenceWidth, useFloatingOverlay } from "./use-floating-overlay"

export interface PopoverProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** The trigger element (rendered inline). */
  trigger: ReactNode
  /**
   * Classes for the inline wrapper around {@link PopoverProps.trigger}. The
   * wrapper is `relative inline-flex` by default; pass `flex w-full` to let a
   * `w-full` trigger stretch to its container instead of collapsing to content
   * width.
   */
  triggerClassName?: string
  /** Popover content. */
  children: ReactNode
  /** Horizontal edge to anchor the content to. */
  align?: "start" | "end"
  /**
   * Which side of the trigger the content prefers. Defaults to "top", which
   * suits bottom-anchored controls such as a composer toolbar. The content
   * flips to the opposite side when the preferred one has no room, so this is
   * a preference rather than a promise.
   */
  side?: "top" | "bottom" | "left" | "right"
  /** Extra classes for the content panel (width, etc.). */
  contentClassName?: string
  /**
   * Keep the panel at least as wide as the trigger. A `min-w-*` in
   * `contentClassName` may still raise that floor, as `min-w-[max(16rem,var(--v2-reference-width,0px))]`.
   */
  matchTriggerWidth?: boolean
  /** Accessible role for the content panel. */
  role?: "menu" | "dialog" | "listbox"
  "aria-label"?: string
}

/** Gap between the trigger and the panel, in pixels. */
const TRIGGER_GAP = 8

/**
 * Lightweight popover: opens beside its trigger, animates in and out through
 * `useTransitionStyles` (the same v2 motion primitive `Menu` and `Select`
 * use), and closes on outside click or Escape. Non-modal.
 *
 * Placement is collision-aware through {@link useFloatingOverlay}: the panel
 * flips to the opposite side, slides along the edge it would otherwise run
 * past, and caps its own height and width to what is left, scrolling inside
 * rather than spilling off-screen.
 */
export const Popover = ({
  open,
  onOpenChange,
  trigger,
  triggerClassName,
  children,
  align = "start",
  side = "top",
  contentClassName,
  matchTriggerWidth,
  role = "menu",
  "aria-label": ariaLabel,
}: PopoverProps) => {
  const reduce = useReducedMotion()
  const { refs, overlayStyles, context, placement, isPositioned } = useFloatingOverlay({
    open,
    onOpenChange,
    placement: `${side}-${align}`,
    gap: TRIGGER_GAP,
    middleware: matchTriggerWidth ? [matchReferenceWidth()] : undefined,
  })

  const dismiss = useDismiss(context)
  const { getReferenceProps, getFloatingProps } = useInteractions([dismiss])

  /**
   * The side the panel actually landed on, which is what the enter/exit
   * animation travels along once a collision has flipped it.
   */
  const resolvedSide = placement.split("-")[0] as NonNullable<PopoverProps["side"]>
  const travelAxis = resolvedSide === "left" || resolvedSide === "right" ? "X" : "Y"
  const travelSign = resolvedSide === "right" || resolvedSide === "bottom" ? -1 : 1

  /**
   * `isMounted` keeps the panel in the tree through its exit, which a plain
   * `open &&` cannot — that was the previous version's missing close
   * animation, since the panel used to unmount synchronously on close.
   */
  const { isMounted, styles: transitionStyles } = useTransitionStyles(context, {
    duration: reduce ? 0 : { open: DURATION_BASE * 1000, close: DURATION_QUICK * 1000 },
    common: { transitionTimingFunction: EASE_OUT_CSS },
    initial: {
      opacity: 0,
      transform: `translate${travelAxis}(${6 * travelSign}px) scale(0.985)`,
    },
    open: { opacity: 1, transform: "translate(0) scale(1)" },
    close: {
      opacity: 0,
      transform: `translate${travelAxis}(${4 * travelSign}px) scale(0.99)`,
    },
  })

  // Inject a click-to-toggle handler onto the trigger element, preserving any
  // handler it already has.
  const triggerElement = trigger as ReactElement<{
    onClick?: MouseEventHandler
  }>
  const triggerNode = isValidElement(trigger)
    ? cloneElement(triggerElement, {
        onClick: (event) => {
          triggerElement.props.onClick?.(event)
          onOpenChange(!open)
        },
      })
    : trigger

  return (
    <>
      <span
        ref={refs.setReference}
        className={cn("relative inline-flex", triggerClassName)}
        {...getReferenceProps()}
      >
        {triggerNode}
      </span>
      {isMounted ? (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            role={role}
            aria-label={ariaLabel}
            style={{
              ...overlayStyles,
              ...transitionStyles,
              /**
               * Held back until the first measurement lands, so the panel never
               * paints at the viewport origin on the frame it mounts. Gated on
               * `open` too — floating-ui resets `isPositioned` to `false` the
               * moment `open` goes false, and the panel stays mounted through
               * its close transition, so without that guard the close
               * animation would render invisible.
               */
              visibility: open && !isPositioned ? "hidden" : undefined,
            }}
            className={cn(
              V2_MENU_SURFACE_CLASS,
              matchTriggerWidth && "min-w-[var(--v2-reference-width,0px)]",
              contentClassName,
            )}
            {...getFloatingProps()}
          >
            {children}
          </div>
        </FloatingPortal>
      ) : null}
    </>
  )
}
